import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 6 algebra: the grammar of expressions before the sentences of
 * equations, then the two places a letter goes next — standing for a whole
 * range of values at once, and standing beside a second letter that moves with
 * it.
 */
export const EXPRESSION_LESSONS: Lesson[] = [
  lesson({
    id: 'algebraic-expressions-01',
    unitId: 'unit-expressions',
    conceptId: 'algebraic-expressions',
    title: 'Algebraic Expressions',
    summary: 'Writing, reading and simplifying expressions — the grammar of algebra, learned before its sentences.',
    objectives: [
      'Translate a description into an algebraic expression',
      'Identify terms, coefficients and constants',
      'Simplify by combining like terms',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Sentences and phrases',
        blocks: [
          text('An equation is a sentence: it makes a claim, and can be true or false.'),
          math('2x + 5 = 11'),
          text('An **expression** is only a phrase. It names a quantity without asserting anything about it.'),
          math('2x + 5'),
          text('You do not solve an expression — there is nothing to solve. You evaluate it, or you tidy it up.'),
        ],
      },
      intuition: {
        title: 'The parts of an expression',
        blocks: [
          text('An expression is built from **terms**, separated by + and − signs. In 3x + 7y − 4:'),
          table(['Part', 'Name', 'Means'], [
            ['3x', 'term', 'three lots of x'],
            ['3', 'coefficient', 'the number multiplying the variable'],
            ['x, y', 'variables', 'numbers we do not know yet'],
            ['−4', 'constant', 'a fixed value, no variable attached'],
          ]),
          insight('**Like terms** contain exactly the same variable. 3x and 5x are like terms; 3x and 5y are not, and neither are 3x and 3x². Only like terms can be combined.'),
          warning('Combining unlike terms is the classic error here. 3x + 5y is already as simple as it gets — writing 8xy is not simplifying, it is changing the value.'),
        ],
      },
      visual: {
        title: 'Why like terms combine',
        blocks: [
          text('Three boxes plus five boxes is eight boxes, whatever a box holds:'),
          math('3x + 5x = (3 + 5)x = 8x'),
          text('But three boxes plus five bottles is not eight of anything. There is no single word for the total, and no shorter way to say it:'),
          math('3x + 5y = 3x + 5y'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A cinema charges £9 a ticket plus a £2 booking fee per order. Write an expression for the cost of n tickets, then simplify 4n + 3 + 2n − 1.'),
          steps([
            { label: 'The part that grows with n.', latex: '9n' },
            { label: 'The fee is charged once, whatever n is.', latex: '+\\,2' },
            { label: 'The expression.', latex: '9n + 2' },
          ]),
          text('Now the simplification:'),
          steps([
            { label: 'Group the like terms.', latex: '(4n + 2n) + (3 - 1)' },
            { label: 'Combine the n terms.', latex: '6n' },
            { label: 'Combine the constants.', latex: '+\\,2' },
            { label: 'Answer.', latex: '6n + 2' },
          ]),
          tip('Check a simplification by substituting a number into both forms. At n = 5 the original gives 32 and so does 6n + 2 — good evidence you have not changed the value.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('algebraic-expressions', 4, 'Simplify by combining like terms.')] },
      independent: { title: 'On your own', blocks: [practice('algebraic-expressions', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('algebraic-expressions', 3, 'Translate a description into an expression.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An expression names a quantity; an equation makes a claim.',
            'Terms are separated by + and −.',
            'Only like terms — the same variable — can be combined.',
            '3x + 5y cannot be simplified.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'one-variable-inequalities-01',
    unitId: 'unit-inequalities-relationships',
    conceptId: 'one-variable-inequalities',
    title: 'Writing and Graphing Inequalities',
    summary: 'An equation names one number. An inequality names every number that would do.',
    objectives: [
      'Translate a condition in words into an inequality',
      'Graph an inequality on the number line',
      'Read an open circle as strict and a closed circle as inclusive',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Most real conditions are not equations',
        blocks: [
          text('A lift holds at most 8 people. A film is for viewers aged 12 and over. A bridge takes loads under 3 tonnes.'),
          text('None of those name a single number, and writing "people = 8" would be false about a lift carrying five. What they describe is a *range*, and an inequality is how a range is written down.'),
          math('p \\le 8 \\qquad a \\ge 12 \\qquad w < 3'),
        ],
      },
      intuition: {
        title: 'Four symbols, and the two that include the edge',
        blocks: [
          text('The open end of the symbol always faces the larger side: in x < 5 the wide end faces 5, so x is the smaller one.'),
          table(['Symbol', 'Read as', 'Is the edge included?'], [
            ['x < 5', 'less than 5', 'No — 5 does not work'],
            ['x ≤ 5', 'at most 5, no more than 5', 'Yes — 5 works'],
            ['x > 5', 'more than 5, over 5', 'No'],
            ['x ≥ 5', 'at least 5, 5 or more', 'Yes'],
          ]),
          warning('"At least 12" means 12 is allowed: a ≥ 12. "More than 12" means it is not: a > 12. English hides this difference in one small word, and it is the difference between a twelve-year-old being let in and being turned away.', 'Where the marks are lost'),
          insight('An inequality usually has infinitely many solutions. That is not a failure to pin the answer down — the answer genuinely is a range, and naming it exactly is the point.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('On the number line, the range is shaded and the endpoint carries the extra information:'),
          figure(
            { kind: 'number-line', from: -2, to: 8, step: 1, ray: { at: 3, direction: 'right', closed: false } },
            'x > 3. The circle is hollow, because 3 itself is not a solution, and the arrow says the shading continues forever.',
          ),
          figure(
            { kind: 'number-line', from: -2, to: 8, step: 1, ray: { at: 3, direction: 'left', closed: true } },
            'x ≤ 3. The circle is filled, because 3 is a solution.',
          ),
          tip('Hollow circle, hollow symbol: < and > leave the endpoint out. The bar underneath ≤ and ≥ is the same bar as in =, and it lets the endpoint in.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A ride requires riders to be at least 140 cm tall. Write an inequality for the allowed heights, decide whether 140 cm gets on, and graph it.'),
          steps([
            { label: 'Name the quantity with a letter, and say what it means.', latex: 'h = \\text{height in cm}' },
            { label: '"At least 140" allows exactly 140, so the edge is included.', latex: 'h \\ge 140' },
            { label: 'A rider of exactly 140 cm satisfies it.', latex: '140 \\ge 140 \\text{ is true}' },
            { label: 'Graph: a filled circle at 140, shaded to the right.', latex: 'h \\ge 140' },
          ]),
          text('Now check a value that should fail, which is how you catch a reversed symbol:'),
          steps([
            { label: 'Test a rider of 132 cm.', latex: '132 \\ge 140' },
            { label: 'That is false, so 132 is not a solution — which is what the ride says.', latex: '\\text{correct}' },
          ]),
          tip('Always substitute one number that should work and one that should not. It costs ten seconds and catches a flipped symbol every time.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('one-variable-inequalities', 4)] },
      independent: { title: 'On your own', blocks: [practice('one-variable-inequalities', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('one-variable-inequalities', 3, 'Conditions in words, where choosing between ≥ and > is the work.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An inequality describes a range of values, not one value.',
            'The wide end of the symbol faces the larger side.',
            '≤ and ≥ include the endpoint; < and > do not.',
            'On a graph: filled circle includes, hollow circle excludes.',
            'Test one value that should work and one that should not.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'two-variable-relationships-01',
    unitId: 'unit-inequalities-relationships',
    conceptId: 'two-variable-relationships',
    title: 'Two Variables at Once',
    summary: 'When one quantity depends on another, the rule, the table and the graph are three views of the same relationship.',
    objectives: [
      'Tell the independent variable from the dependent one',
      'Write an equation relating two quantities',
      'Move between a rule, a table and a graph',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'One letter was never going to be enough',
        blocks: [
          text('So far a letter has stood for one unknown number, waiting to be found. Here both letters vary, and what matters is how they vary *together*.'),
          text('A market stall sells apples at 3 units of currency each. Buy 1 and you pay 3; buy 4 and you pay 12. The cost is not a mystery to be solved — it follows the number of apples wherever it goes.'),
          math('c = 3a'),
        ],
      },
      intuition: {
        title: 'Which one is in charge?',
        blocks: [
          text('One of the two quantities is chosen freely; the other answers. The one you choose is the **independent** variable. The one that responds is the **dependent** variable — it depends on the first.'),
          table(['Situation', 'Independent (chosen)', 'Dependent (follows)'], [
            ['Apples at 3 each', 'apples bought', 'cost'],
            ['Driving at 60 km/h', 'hours driven', 'distance covered'],
            ['A tank draining', 'minutes elapsed', 'litres left'],
          ]),
          warning('The order is not decoration. You decide how many apples to buy and the cost follows; you do not decide the cost and have apples appear. Getting this backwards puts the axes the wrong way round and makes the graph describe a different situation.', 'Which depends on which'),
          insight('The dependent variable goes on the vertical axis, always. That is the convention every graph you will ever read follows, and it is why "y depends on x" is such a common sentence.'),
        ],
      },
      visual: {
        title: 'Three views of one relationship',
        blocks: [
          text('The rule, in symbols:'),
          math('c = 3a'),
          text('The same rule, as a table:'),
          table(['Apples a', 'Cost c'], [['0', '0'], ['1', '3'], ['2', '6'], ['3', '9'], ['4', '12']]),
          text('And the same rule again, as points on a grid:'),
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 0, y: 0 },
                { x: 1, y: 3 },
                { x: 2, y: 6 },
                { x: 3, y: 9 },
                { x: 4, y: 12 },
              ],
            },
            'Apples across, cost up. Each row of the table is one point, and they fall in a straight line because every apple adds the same 3.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A tap fills a tank at 8 litres per minute, starting from empty. Write an equation, build a short table, and say what the point (5, 40) means.'),
          steps([
            { label: 'Choose letters and say what they mean.', latex: 't = \\text{minutes}, \\quad v = \\text{litres in the tank}' },
            { label: 'Time is chosen; volume follows. So volume is dependent.', latex: 'v \\text{ depends on } t' },
            { label: 'Each minute adds 8 litres.', latex: 'v = 8t' },
            { label: 'Table: substitute t = 1, 2, 3.', latex: '8, \\; 16, \\; 24' },
            { label: 'The point (5, 40) is t first, v second: after 5 minutes there are 40 litres.', latex: 'v = 8 \\times 5 = 40' },
          ]),
          tip('Read a point in the order the axes are drawn: across, then up. (5, 40) and (40, 5) describe very different afternoons.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('two-variable-relationships', 4)] },
      independent: { title: 'On your own', blocks: [practice('two-variable-relationships', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('two-variable-relationships', 3, 'Move between the rule, the table and the graph.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The independent variable is chosen; the dependent one follows.',
            'The dependent variable goes on the vertical axis.',
            'A rule, a table and a graph are three views of one relationship.',
            'Substituting a value into the rule is what fills in the table.',
          ]),
        ],
      },
    },
  }),
];
